import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  MessageSquare,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Hash,
  Clock,
  ArrowLeft,
  User as UserIcon,
  Users2,
  Image as ImageIcon,
  Mic,
  Info,
  Lock,
  Shield,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Avatar from './Avatar';
import {
  useAdminConversations,
  useAdminConversationStats,
  useAdminConversationDetail,
  useAdminConversationMessages,
  useAdminMessageLogsPinMeta,
  useUnlockAdminMessageLogs,
  useUsers,
} from '../hooks/api';

const TYPE_META = {
  direct: {
    label: 'Direct',
    Icon: UserIcon,
    pill: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  },
  group: {
    label: 'Group',
    Icon: Users2,
    pill: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, tone = 'primary' }) => {
  const tones = {
    primary: 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  };
  return (
    <div className="bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-2xl shadow-sm p-4 flex items-center gap-4">
      <div className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold text-secondary-900 dark:text-white leading-tight">
          {value}
        </div>
        <div className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">{label}</div>
      </div>
    </div>
  );
};

const TypePill = ({ type }) => {
  const meta = TYPE_META[type] || TYPE_META.direct;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.pill}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
};

const MessageBody = ({ message }) => {
  if (message.is_deleted) {
    return (
      <p className="italic text-secondary-500 dark:text-secondary-400">
        [deleted]
        {message.deleted_by_name ? ` by ${message.deleted_by_name}` : ''}
        {message.deleted_at ? ` · ${formatDate(message.deleted_at)}` : ''}
      </p>
    );
  }
  if (message.type === 'image' && message.image_url) {
    return (
      <div className="space-y-1">
        {message.content ? <p>{message.content}</p> : null}
        <a href={message.image_url} target="_blank" rel="noreferrer" className="block">
          <img
            src={message.image_url}
            alt="Chat attachment"
            className="max-h-48 rounded-lg border border-secondary-200 dark:border-secondary-600"
          />
        </a>
      </div>
    );
  }
  if (message.type === 'voice' && message.image_url) {
    return (
      <audio controls src={message.image_url} className="max-w-full">
        Voice message
      </audio>
    );
  }
  if (message.type === 'system') {
    return (
      <p className="text-xs text-secondary-500 dark:text-secondary-400 italic text-center w-full">
        {message.content || 'System event'}
      </p>
    );
  }
  return <p className="whitespace-pre-wrap break-words">{message.content || ''}</p>;
};

const TypeBadge = ({ type }) => {
  if (type === 'image') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide text-secondary-400">
        <ImageIcon className="h-3 w-3" /> Image
      </span>
    );
  }
  if (type === 'voice') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide text-secondary-400">
        <Mic className="h-3 w-3" /> Voice
      </span>
    );
  }
  if (type === 'system') {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide text-secondary-400">
        <Info className="h-3 w-3" /> System
      </span>
    );
  }
  return null;
};

const MessageLogsPinGate = ({ onUnlock }) => {
  const { data: pinMeta, isLoading: metaLoading, isError: metaError } =
    useAdminMessageLogsPinMeta(true);
  const unlockMutation = useUnlockAdminMessageLogs();
  const pinLength = pinMeta?.configured ? pinMeta.pin_length : 0;
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);
  const verifying = unlockMutation.isPending;

  useEffect(() => {
    if (pinLength > 0) inputRef.current?.focus();
  }, [pinLength]);

  const failUnlock = useCallback((message) => {
    setError(message || 'Incorrect PIN. Try again.');
    setShake(true);
    setPin('');
    window.setTimeout(() => setShake(false), 450);
    inputRef.current?.focus();
  }, []);

  const tryUnlock = useCallback(
    async (value) => {
      if (!pinMeta?.configured || verifying) return false;
      if (value.length !== pinLength) return false;
      try {
        await unlockMutation.mutateAsync(value);
        setError('');
        onUnlock();
        return true;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        failUnlock(
          typeof detail === 'string' ? detail : 'Incorrect PIN. Try again.'
        );
        return false;
      }
    },
    [failUnlock, onUnlock, pinLength, pinMeta?.configured, unlockMutation, verifying]
  );

  const setPinDigits = useCallback(
    (next) => {
      if (!pinLength || verifying) return;
      const digits = String(next).replace(/\D/g, '').slice(0, pinLength);
      setPin(digits);
      setError('');
      if (digits.length === pinLength) {
        tryUnlock(digits);
      }
    },
    [pinLength, tryUnlock, verifying]
  );

  const submitPin = (e) => {
    e?.preventDefault?.();
    if (!pinMeta?.configured) {
      setError('Message Logs PIN is not configured on the server.');
      return;
    }
    if (pin.length < pinLength) {
      setError(`Enter the ${pinLength}-digit PIN.`);
      return;
    }
    tryUnlock(pin);
  };

  const pressDigit = (digit) => {
    if (verifying || pin.length >= pinLength) return;
    setPinDigits(pin + digit);
  };

  const pressBackspace = () => {
    if (verifying) return;
    setError('');
    setPin((prev) => prev.slice(0, -1));
    inputRef.current?.focus();
  };

  const keypad = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];
  const configBlocked =
    metaError || (pinMeta && pinMeta.configured === false);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm">
        <form
          onSubmit={submitPin}
          className={`rounded-2xl border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 shadow-lg shadow-secondary-200/60 dark:shadow-none p-6 sm:p-7 space-y-6 ${
            shake ? 'translate-x-0' : ''
          }`}
          style={
            shake
              ? { animation: 'message-logs-pin-shake 0.45s ease-in-out' }
              : undefined
          }
          onClick={() => !configBlocked && inputRef.current?.focus()}
        >
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-amber-50 dark:bg-amber-900/40 border border-amber-100 dark:border-amber-800/50 flex items-center justify-center">
              <Lock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
                Enter access PIN
              </h2>
              <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1 max-w-[18rem] mx-auto leading-relaxed">
                Only authorized admins can view conversation transcripts.
              </p>
            </div>
          </div>

          {metaLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : configBlocked ? (
            <p className="text-sm text-center text-red-600 dark:text-red-400">
              {pinMeta?.detail ||
                'Message Logs PIN is not configured. Set MESSAGE-LOGS-PIN in Key Vault (or MESSAGE_LOGS_PIN locally).'}
            </p>
          ) : (
            <>
              <div className="relative">
                <input
                  ref={inputRef}
                  id="message-logs-pin"
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={pin}
                  onChange={(e) => setPinDigits(e.target.value)}
                  disabled={verifying}
                  className="absolute inset-0 w-full h-full opacity-0 caret-transparent cursor-text"
                  aria-label={`${pinLength}-digit access PIN`}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'message-logs-pin-error' : undefined}
                  maxLength={pinLength}
                />
                <div
                  className="flex items-center justify-center gap-2.5 sm:gap-3"
                  aria-hidden="true"
                >
                  {Array.from({ length: pinLength }).map((_, i) => {
                    const filled = i < pin.length;
                    const active = i === pin.length && !error && !verifying;
                    return (
                      <div
                        key={i}
                        className={`h-12 w-10 sm:h-14 sm:w-11 rounded-xl border-2 flex items-center justify-center transition-all ${
                          error
                            ? 'border-red-400 bg-red-50 dark:bg-red-900/20'
                            : filled
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                              : active
                                ? 'border-primary-400 bg-white dark:bg-secondary-900 ring-2 ring-primary-200 dark:ring-primary-800'
                                : 'border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-900/50'
                        }`}
                      >
                        {filled ? (
                          <span className="h-2.5 w-2.5 rounded-full bg-secondary-800 dark:bg-secondary-100" />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                {error ? (
                  <p
                    id="message-logs-pin-error"
                    className="mt-3 text-sm font-medium text-red-600 dark:text-red-400 text-center"
                  >
                    {error}
                  </p>
                ) : (
                  <p className="mt-3 text-xs text-secondary-400 text-center">
                    {verifying
                      ? 'Verifying…'
                      : `${pin.length}/${pinLength} digits`}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2.5 select-none">
                {keypad.map((key, idx) => {
                  if (key === '') {
                    return <div key={`empty-${idx}`} />;
                  }
                  if (key === '⌫') {
                    return (
                      <button
                        key="backspace"
                        type="button"
                        onClick={pressBackspace}
                        disabled={verifying}
                        className="h-12 rounded-xl border border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-900/60 text-secondary-700 dark:text-secondary-200 text-lg font-medium hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors disabled:opacity-50"
                        aria-label="Delete last digit"
                      >
                        ⌫
                      </button>
                    );
                  }
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => pressDigit(key)}
                      disabled={verifying}
                      className="h-12 rounded-xl border border-secondary-200 dark:border-secondary-600 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white text-lg font-semibold hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50"
                    >
                      {key}
                    </button>
                  );
                })}
              </div>

              <button
                type="submit"
                disabled={pin.length < pinLength || verifying}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 shadow-sm transition-colors"
              >
                <Shield className="h-4 w-4" />
                {verifying ? 'Verifying…' : 'Unlock Message Logs'}
              </button>
            </>
          )}
        </form>

        <style>{`
          @keyframes message-logs-pin-shake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-6px); }
            80% { transform: translateX(6px); }
          }
        `}</style>
      </div>
    </div>
  );
};

const AdminMessageLogs = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [unlocked, setUnlocked] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [filters, setFilters] = useState({
    type: 'all',
    user_id: '',
    q: '',
    date_from: '',
    date_to: '',
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const searchDebounce = useRef(null);

  const canLoad = isAdmin && unlocked;

  const listFilters = useMemo(
    () => ({
      page,
      page_size: pageSize,
      type: filters.type === 'all' ? undefined : filters.type,
      user_id: filters.user_id || undefined,
      q: filters.q || undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined,
    }),
    [page, pageSize, filters]
  );

  const {
    data: listData,
    isLoading: listLoading,
    isFetching: listFetching,
    refetch: refetchList,
    isError: listError,
  } = useAdminConversations(listFilters, canLoad);

  const {
    data: stats,
    refetch: refetchStats,
  } = useAdminConversationStats(canLoad);

  const { data: usersData } = useUsers();
  const teamMembers = useMemo(() => {
    const raw = Array.isArray(usersData) ? usersData : usersData?.results || [];
    return raw.filter((u) => u.role === 'member' || u.role === 'project_lead');
  }, [usersData]);

  const {
    data: detail,
    isLoading: detailLoading,
  } = useAdminConversationDetail(selectedId, canLoad && !!selectedId);

  const {
    data: messagesData,
    isLoading: messagesLoading,
    refetch: refetchMessages,
  } = useAdminConversationMessages(selectedId, canLoad && !!selectedId);

  const items = listData?.items || [];
  const total = listData?.total || 0;
  const totalPages = useMemo(
    () => (total ? Math.max(1, Math.ceil(total / pageSize)) : 1),
    [total, pageSize]
  );
  const messages = messagesData?.messages || [];

  useEffect(() => {
    if (listError) {
      toast.error('Failed to load message logs');
    }
  }, [listError]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setFilters((f) => ({ ...f, q: searchInput.trim() }));
      setPage(1);
    }, 350);
    return () => searchDebounce.current && clearTimeout(searchDebounce.current);
  }, [searchInput]);

  const resetFilters = useCallback(() => {
    setFilters({ type: 'all', user_id: '', q: '', date_from: '', date_to: '' });
    setSearchInput('');
    setPage(1);
  }, []);

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => {
    if (k === 'type') return v && v !== 'all';
    return Boolean(v);
  }).length;

  const closeModal = () => setSelectedId(null);

  const lockAgain = () => {
    setSelectedId(null);
    setUnlocked(false);
  };

  const refresh = () => {
    refetchList();
    refetchStats();
    if (selectedId) refetchMessages();
  };

  if (user && !isAdmin) {
    return (
      <div className="card p-8 text-center">
        <h2 className="text-lg font-medium text-secondary-900 dark:text-white">
          Admin access required
        </h2>
        <p className="text-sm text-secondary-500 mt-1">
          You don&apos;t have permission to view message logs.
        </p>
      </div>
    );
  }

  if (!unlocked) {
    return <MessageLogsPinGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div className="space-y-6">
      <div className="page-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate('/dashboard');
            }}
            className="p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 text-secondary-600 dark:text-secondary-300"
            title="Back"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
              Message Logs
            </h1>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
              Browse every platform DM and group chat transcript.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={lockAgain}
            className="btn-secondary flex items-center gap-2"
            title="Lock Message Logs"
          >
            <Lock className="h-4 w-4" />
            Lock
          </button>
          <button
            type="button"
            onClick={refresh}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${listFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={MessageSquare} tone="primary" label="Total chats" value={stats.total_chats} />
          <StatCard icon={UserIcon} tone="blue" label="Direct messages" value={stats.total_dms} />
          <StatCard icon={Users2} tone="purple" label="Groups" value={stats.total_groups} />
          <StatCard
            icon={Clock}
            tone="green"
            label="Messages (7 days)"
            value={stats.messages_last_7_days}
          />
        </div>
      )}

      <div className="card p-4">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              Search name or last message
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="e.g. Alice, standup..."
                className="input pl-9 w-full"
              />
            </div>
          </div>

          <div className="w-full lg:w-40">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => {
                setFilters((f) => ({ ...f, type: e.target.value }));
                setPage(1);
              }}
              className="input w-full"
            >
              <option value="all">All</option>
              <option value="direct">Direct</option>
              <option value="group">Group</option>
            </select>
          </div>

          <div className="w-full lg:w-56">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              Participant
            </label>
            <select
              value={filters.user_id}
              onChange={(e) => {
                setFilters((f) => ({ ...f, user_id: e.target.value }));
                setPage(1);
              }}
              className="input w-full"
            >
              <option value="">All users</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name || m.username || m.email}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full lg:w-40">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              From
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => {
                setFilters((f) => ({ ...f, date_from: e.target.value }));
                setPage(1);
              }}
              className="input w-full"
            />
          </div>

          <div className="w-full lg:w-40">
            <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
              To
            </label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => {
                setFilters((f) => ({ ...f, date_to: e.target.value }));
                setPage(1);
              }}
              className="input w-full"
            />
          </div>

          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={resetFilters}
              className="btn-secondary flex items-center gap-2 whitespace-nowrap"
              title="Clear filters"
            >
              <X className="h-4 w-4" />
              Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      <div className="card overflow-hidden">
        {listLoading ? (
          <div className="py-16 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <MessageSquare className="h-10 w-10 text-secondary-300 dark:text-secondary-600 mx-auto mb-3" />
            <p className="text-secondary-600 dark:text-secondary-300">No conversations found</p>
            <p className="text-xs text-secondary-500 mt-1">Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
              <thead className="bg-secondary-50 dark:bg-secondary-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Participants / Group
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Last message
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Msgs
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                    Updated
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                {items.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => setSelectedId(row.id)}
                    className="hover:bg-secondary-50 dark:hover:bg-secondary-800/60 cursor-pointer transition"
                  >
                    <td className="px-4 py-3">
                      <TypePill type={row.type} />
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                        {row.name}
                      </div>
                      {row.type === 'group' && (
                        <div className="text-xs text-secondary-500 mt-0.5">
                          {row.member_count || row.participant_names?.length || 0} members
                        </div>
                      )}
                      {row.type === 'direct' && row.participant_names?.length > 0 && (
                        <div className="text-xs text-secondary-500 mt-0.5 truncate">
                          {row.participant_names.join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <div className="text-sm text-secondary-700 dark:text-secondary-300 truncate">
                        {row.last_message_preview || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-700 dark:text-secondary-300">
                      {row.message_count}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-700 dark:text-secondary-300 whitespace-nowrap">
                      {formatDate(row.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!listLoading && items.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-secondary-200 dark:border-secondary-700">
            <div className="text-sm text-secondary-600 dark:text-secondary-400">
              Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span>–
              <span className="font-medium">{Math.min(page * pageSize, total)}</span> of{' '}
              <span className="font-medium">{total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <span className="text-sm text-secondary-600 dark:text-secondary-400">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-secondary px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={closeModal}
        >
          <div
            className="bg-white dark:bg-secondary-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-secondary-200 dark:border-secondary-700 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {detail && <TypePill type={detail.type || 'direct'} />}
                  {detail?.member_count != null && (
                    <span className="text-xs text-secondary-500">
                      • {detail.member_count} participant
                      {detail.member_count === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <h2 className="text-lg font-semibold text-secondary-900 dark:text-white truncate">
                  {detail?.name || 'Conversation'}
                </h2>
                {detail && (
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-secondary-500">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Updated {formatDate(detail.updated_at)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Hash className="h-3 w-3" />
                      {detail.message_count ?? messages.length} messages
                    </span>
                  </div>
                )}
                {detail?.participants?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {detail.participants.map((p) => (
                      <span
                        key={p.id}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-secondary-100 dark:bg-secondary-800 text-xs text-secondary-700 dark:text-secondary-300"
                      >
                        <Avatar
                          blobName={p.avatar}
                          fallbackText={(p.full_name || '?').slice(0, 1)}
                          size="xs"
                          alt={p.full_name}
                        />
                        {p.full_name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="p-1.5 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-secondary-50/40 dark:bg-secondary-950/40">
              {detailLoading || messagesLoading ? (
                <div className="py-16 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : messages.length === 0 ? (
                <div className="py-10 text-center text-sm text-secondary-500">
                  No messages in this conversation.
                </div>
              ) : (
                messages.map((m) => {
                  if (m.type === 'system') {
                    return (
                      <div key={m.id} className="flex justify-center py-1">
                        <div className="max-w-[90%] px-3 py-1.5 rounded-full bg-secondary-100 dark:bg-secondary-800 text-center">
                          <MessageBody message={m} />
                          <div className="text-[10px] text-secondary-400 mt-0.5">
                            {formatDate(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className="flex gap-3">
                      <Avatar
                        blobName={m.sender_avatar}
                        fallbackText={(m.sender_name || '?').slice(0, 1)}
                        size="sm"
                        alt={m.sender_name}
                      />
                      <div
                        className={`max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm border ${
                          m.is_deleted
                            ? 'bg-secondary-100/80 dark:bg-secondary-800/60 border-dashed border-secondary-300 dark:border-secondary-600'
                            : 'bg-white dark:bg-secondary-800 border-secondary-200 dark:border-secondary-700'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-secondary-800 dark:text-secondary-200">
                            {m.sender_name || 'Unknown'}
                          </span>
                          <TypeBadge type={m.type} />
                          {m.is_deleted && (
                            <span className="text-[10px] font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400">
                              Deleted
                            </span>
                          )}
                        </div>
                        <div className="text-secondary-900 dark:text-white">
                          <MessageBody message={m} />
                        </div>
                        <div className="mt-1 text-[10px] text-secondary-400">
                          {formatDate(m.created_at)}
                          {m.edited_at ? ` · edited ${formatDate(m.edited_at)}` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              {messagesData?.has_more && (
                <p className="text-center text-xs text-secondary-500 pt-2">
                  Showing latest {messages.length} messages. Older messages exist in this chat.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMessageLogs;
